import { setLayerRuntimeValue } from '@/utils/layer-kit';
import { BaseClassInstance, IFont, ILayerProps, IRegisterPluginOptions } from '@/version-types';
import { FontServer } from '../extensions/font-server';
import { RegisterServer } from '../extensions/register-server';
import { CoreData } from './core-data';

/** 核心服务类 */
export class CoreServer extends CoreData {
    /** 字体服务 */
    protected fontServer: FontServer;
    /** 注册服务 */
    protected registerServer: RegisterServer;

    constructor(identity: string) {
        super(identity);
        this.fontServer = new FontServer(identity);
        this.registerServer = new RegisterServer(identity, this.config);
    }

    destroy() {
        this.fontServer.destroy();
        super.destroy();
    }

    /** 字体 *************************************************/

    /**
     * 设置字体列表
     *
     * @param fontList - 字体列表
     */
    setFontList(fontList: IFont[]) {
        this.fontServer.setList(fontList);
    }

    /**
     * 获取字体列表
     */
    getFontList() {
        return this.fontServer.getList();
    }

    /**
     * 获取字体
     *
     * @param fontName - 字体名称
     * @returns IFont
     */
    getFont(fontName: string) {
        return this.fontServer.getFont(fontName);
    }

    /**
     * 获取正在使用的字体列表
     *
     * @param originIndex - 舞台原始索引
     */
    getUsingFontList(originIndex: number): IFont[];

    /**
     * 获取正在使用的字体列表
     *
     * @param layers - 图层数据
     */
    getUsingFontList(layers: ILayerProps[]): IFont[];
    getUsingFontList(param: number | ILayerProps[]): IFont[] {
        const layers = Array.isArray(param) ? param : this.getLayers(param);
        return this.fontServer.getUsingFontList(layers);
    }

    /**
     * 获取图层字体 Base64 编码
     *
     * @param layer - 图层属性
     */
    getFontBase64(layer: ILayerProps) {
        return this.fontServer.getLayerFontBase64(layer) || {};
    }

    /**
     * 创建字体样式
     *
     * @param fontName - 字体名称
     * @param fontBase64 - 字体 Base64 编码
     * @param isWoff2 - 可选；是否是 woff2 格式；默认值为 `true`
     */
    createFontStyle(fontName: string, fontBase64: string, isWoff2: boolean = true) {
        return this.fontServer.createFontStyle(fontName, fontBase64, isWoff2);
    }

    /**
     * 创建字体子集
     *
     * @param originIndex - 舞台原始索引
     * @param refreshLayer - 是否重新渲染图层，【下载时不需要重新渲染】
     */
    async createFontFamilySubset(originIndex: number, refreshLayer: boolean): Promise<ILayerProps[]>;

    /**
     * 创建字体子集
     *
     * @param layers - 图层数据
     * @param refreshLayer - 是否重新渲染图层，【下载时不需要重新渲染】
     */
    async createFontFamilySubset(layers: ILayerProps[], refreshLayer: boolean): Promise<ILayerProps[]>;
    async createFontFamilySubset(param: number | ILayerProps[], refreshLayer: boolean) {
        const layers = Array.isArray(param) ? param : this.getLayers(param);
        const refreshLayers = await this.fontServer.createLayersFontSubset(layers, refreshLayer);
        if (refreshLayer && refreshLayers.length) {
            refreshLayers.forEach((layer) => {
                const latestLayer = this.getLayer(layer.id, true);
                if (latestLayer) {
                    setLayerRuntimeValue(latestLayer, {
                        fontFamilyMap: layer.__runtime?.fontFamilyMap,
                    });
                    this.flatLayersMap.set(latestLayer.id, latestLayer);
                }
            });
            this.rerender();
        }
        return refreshLayers;
    }

    /**
     * 注册全量字体
     *
     * @param layer - 图层属性
     * @param fontName - 字体名称
     * @param showLoading - 可选；是否显示加载动画；默认值为 `true`
     * @returns 字体 material_id
     */
    async registerFont(layer: ILayerProps, fontName: string, showLoading: boolean = true) {
        const font = this.fontServer.getFont(fontName);
        if (!font) return '';

        if (showLoading) {
            this.setLoading(layer.id, fontName);
        }

        await this.fontServer.registerFont(layer, fontName);

        const latestLayer = this.getLayer(layer.id, true);
        if (latestLayer) {
            setLayerRuntimeValue(latestLayer, {
                fontFamilyMap: layer.__runtime?.fontFamilyMap,
            });
            this.flatLayersMap.set(latestLayer.id, latestLayer);
            this.rerender();
        }

        if (showLoading) {
            this.removeLoading(layer.id, fontName);
        }
        return font.material_id;
    }

    /**
     * 加载字体
     *
     * @param fontName - 字体名称
     * @param zip_url - 字体 zip 地址
     */
    async loadFont(fontName: string, zipUrl: string) {
        await this.fontServer.loadFont(fontName, zipUrl);
    }

    /**
     * 创建文本字体子集
     *
     * @param texts - 文本
     * @param fontName - 字体名称
     */
    async createTextSubset(texts: string, fontName: string) {
        return await this.fontServer.createTextFontSubset(texts, fontName);
    }

    /**
     * 获取字体子集 Base64 编码
     *
     * @param subsetName - 字体子集名称
     */
    getSubsetBase64(subsetName: string) {
        return this.fontServer.getSubsetFontBase64(subsetName);
    }

    /** 注册服务 *************************************************/

    /**
     * 根据类名获取实例 - 重载1：传入构造函数，返回该构造函数的实例类型
     *
     * @param classConstructor - 类构造函数
     */
    getInstance<T extends BaseClassInstance>(classConstructor: T): InstanceType<T>;
    /**
     * 根据类名获取实例 - 重载2：传入字符串名称，返回泛型类型
     *
     * @param name - 类名字符串
     */
    getInstance<T = any>(name: string): T;
    /**
     * 根据类名获取实例 - 实现
     */
    getInstance<T extends BaseClassInstance>(classConstructorOrName: T | string): InstanceType<T> | any {
        if (typeof classConstructorOrName === 'string') {
            return this.registerServer.getInstanceByName<T>(classConstructorOrName);
        }
        return this.registerServer.getInstance<T>(classConstructorOrName);
    }

    /**
     * 注册插件
     *
     * @param plugin - 插件
     */
    registerPlugins(configs: IRegisterPluginOptions[]) {
        this.registerServer.registerPlugins(configs);
    }

    /**
     * 获取插件
     */
    getPlugins() {
        return this.registerServer.getPlugins();
    }

    /**
     * 获取注册销毁列表
     */
    destroyPlugins() {
        this.registerServer.destroyList.forEach((fn) =>
            fn({ identity: this.identity, getInstance: this.getInstance.bind(this) }),
        );
    }

    /**
     * 创建历史记录
     */
    createHistory() {
        this.registerServer.historyList.forEach((fn) => fn({ identity: this.identity }));
    }
}
